import React, { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as tripPackageInventorySlice from '../../../data/tripPackageInventory.slice';
import * as referenceSlice from '../../../data/reference.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import * as passengerSlice from '../../../data/passenger.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid';
import { TripLegAddress } from '../../../data/models/trip-leg.model';
import TripPackageRejectDialog from './TripPackageRejectDialog';
import { TransportationAttribute } from '../../../data/models/transportation-attribute.model';
import dateUtils from '../../../services/DateUtils';
import TripPackageSummary from './TripPackageSummary';
import { financialUtils } from '../../../services/financialUtils';

const PREFIX = 'TripPackageDialog';

const classes = {
  uploadButton: `${PREFIX}-uploadButton`,
  form: `${PREFIX}-form`,
  control: `${PREFIX}-control`,
  grid: `${PREFIX}-grid`,
  address: `${PREFIX}-address`,
  line2: `${PREFIX}-line2`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.uploadButton}`]: {
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.form}`]: {
    margin: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.control}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.grid}`]: {
    marginTop: theme.spacing(2),
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },

  [`& .${classes.address}`]: {
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.line2}`]: {
    color: 'rgba(1, 16, 18, 0.60)',
    fontWeight: 400,
    fontSize: '0.675rem',
  },
}));

interface TripPackageDialogProps {
  open: boolean;
  packageUUID: string;
  close: () => void;
}

export default function TripPackageDialog({
  open,
  packageUUID,
  close,
}: TripPackageDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const passengers = useAppSelector(passengerSlice.selectpassengers);
  const timezones = useAppSelector(referenceSlice.selectTimeZones);

  const rejectReasons = useAppSelector(
    referenceSlice.selectTripLegPackageRejectReasons,
  );

  const [tripPackageRejectOpen, setTripPackageRejectOpen] =
    React.useState(false);

  const loadingTripPackage = useAppSelector(
    tripPackageInventorySlice.selectLoadingTripLegPackage,
  );
  const tripPackage = useAppSelector(
    tripPackageInventorySlice.selectTripLegPackage,
  );
  const acceptingTripPackage = useAppSelector(
    tripPackageInventorySlice.selectAcceptingTripPackage,
  );
  const rejectingTripPackage = useAppSelector(
    tripPackageInventorySlice.selectRejectingTripPackage,
  );
  const transportationAttributes = useAppSelector(
    accountingSlice.selectAllTransportationAttributes,
  );
  const offeredTripLegEvent = useAppSelector(
    tripPackageInventorySlice.selectOfferedTripLegEvent,
  );

  React.useEffect(() => {
    dispatch(passengerSlice.clearPassengers());
    dispatch(accountingSlice.fetchTransportationAttributesAsync());
  }, []);

  React.useEffect(() => {
    if (packageUUID) {
      dispatch(tripPackageInventorySlice.getTripLegPackageAsync(packageUUID));
    }
  }, [packageUUID]);

  React.useEffect(() => {
    if (offeredTripLegEvent && packageUUID) {
      dispatch(tripPackageInventorySlice.getTripLegPackageAsync(packageUUID));
      dispatch(tripPackageInventorySlice.setOfferedTripLegEvent(false));
    }
  }, [offeredTripLegEvent, packageUUID, dispatch]);

  React.useEffect(() => {
    if (tripPackage) {
      tripPackage.tripLegs.forEach((tl) => {
        dispatch(passengerSlice.getPassengerAsync(tl.passengerUUID));
      });
    }
  }, [tripPackage]);

  const acceptTripPackage = () => {
    dispatch(tripPackageInventorySlice.acceptTripPackageAsync(packageUUID));
    close();
  };

  const rejectTripPackage = () => {
    setTripPackageRejectOpen(true);
  };

  const closeDialog = () => {
    close();
  };

  const renderPickupTime = (params: GridCellParams): React.ReactElement => {
    const isWillCall = params.row.isWillCall;

    const tz = isWillCall
      ? params.row.dropOffAddress.timeZone
      : params.row.pickUpAddress.timeZone;
    return isWillCall ? (
      <Box>
        <Typography>
          {dateUtils.formatAppointmentDate(params.row.appointmentDate, tz)}{' '}
          {t('trips.willCall')}
        </Typography>
        <Typography className={classes.line2}>
          {params.row.tripLegReferenceID}
        </Typography>
      </Box>
    ) : (
      <Box>
        <Typography>
          {dateUtils.formatPickupDateTime(params.row.pickUpDueDateTime, tz)}
        </Typography>
        <Typography className={classes.line2}>
          {params.row.tripLegReferenceID}
        </Typography>
      </Box>
    );
  };

  const renderAddress = (address: TripLegAddress) => {
    return (
      <Box className={classes.address}>
        <Typography>{`${address.addressLine1}`}</Typography>
        <Typography
          className={
            classes.line2
          }>{`${address.city}, ${address.stateCode} ${address.zipCode}`}</Typography>
      </Box>
    );
  };

  const renderPassenger = (params: GridCellParams): React.ReactElement => {
    const passengerUUID = params.row.passengerUUID;
    const passenger = passengers[passengerUUID];
    const name = passenger
      ? `${passenger.firstName ?? ''} ${passenger.lastName ?? ''}`
      : '';
    const count = params.row.companionCount + 1;
    const key =
      count === 1
        ? 'notification.passengerCount'
        : 'notification.passengersCount';
    return (
      <Box>
        <Typography>{name}</Typography>
        <Typography className={classes.line2}>{t(key, { count })}</Typography>
      </Box>
    );
  };

  const renderPickupAddress = (params: GridCellParams): React.ReactElement => {
    return renderAddress(params.row.pickUpAddress);
  };

  const renderDropoffAddress = (params: GridCellParams): React.ReactElement => {
    return renderAddress(params.row.dropOffAddress);
  };

  const getAttributeByUUID = (uuid: string) => {
    return transportationAttributes.find(
      (a) => a.transportationAttributeUUID === uuid,
    );
  };

  const renderTransportationAttributes = (
    params: GridCellParams,
  ): React.ReactElement => {
    const [top, rest]: [TransportationAttribute[], TransportationAttribute[]] =
      params.row.transportationAttributes.map((uuids: string[]) =>
        uuids.map(getAttributeByUUID),
      );

    const tripModes = top
      .filter(
        (attr): attr is TransportationAttribute =>
          attr?.transportationAttributeCategoryBC !== 'AMBULATORY',
      )
      .concat(
        rest.filter(
          (attr) => attr.transportationAttributeCategoryBC === 'MOBILITY_AID',
        ),
      );
    const careLevels = rest.filter(
      (attr): attr is TransportationAttribute =>
        attr?.transportationAttributeCategoryBC !== 'TRANSPORTMOBILITY' &&
        attr?.transportationAttributeCategoryBC !== 'AMBULATORY' &&
        attr?.transportationAttributeCategoryBC !== 'MOBILITY_AID',
    );
    return (
      <Box>
        <Typography>
          {tripModes
            .reverse()
            .map((a) => {
              if (/\((\w+)\)/.test(a.name)) {
                return a.name.replace(/.*\((\w+)\).*/, '$1');
              }
              return a.passengerCareLevelBC ?? a.name;
            })
            .join(', ')}
        </Typography>
        <Typography className={classes.line2}>
          {careLevels.map((a) => a.name).join(', ')}
        </Typography>
      </Box>
    );
  };

  const renderDistance = (params: GridCellParams): React.ReactElement => {
    return <>{params.row.estimatedDistanceMiles || ''}</>;
  };

  const renderPrice = (params: GridCellParams): React.ReactElement => {
    const payableAmount =
      params.row.tripLegServicePrice?.payableAmount ?? params.row.payableAmount;
    return (
      <>{payableAmount ? financialUtils.formatPrice(payableAmount) : ''}</>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: t('notification.pickupTime') || '',
      width: 180,
      sortable: false,
      renderCell: renderPickupTime,
    },
    {
      field: 'pickupAddress',
      headerName: t('notification.pickupAddress') || '',
      flex: 1,
      sortable: false,
      renderCell: renderPickupAddress,
    },
    {
      field: 'dropoffAddress',
      headerName: t('notification.dropoffAddress') || '',
      flex: 1,
      sortable: false,
      renderCell: renderDropoffAddress,
    },
    {
      field: 'passenger',
      headerName: t('notification.passenger') || '',
      flex: 1,
      sortable: false,
      renderCell: renderPassenger,
    },
    {
      field: 'attributes',
      headerName: t('notification.serviceLevel') || '',
      flex: 1,
      sortable: false,
      renderCell: renderTransportationAttributes,
    },
    {
      field: 'distance',
      headerName: t('notification.distance') || '',
      width: 80,
      sortable: false,
      renderCell: renderDistance,
    },
    {
      field: 'price',
      headerName: t('notification.price') || '',
      width: 80,
      sortable: false,
      renderCell: renderPrice,
    },
  ];

  const closeTripPackageReject = (reason?: string) => {
    if (reason) {
      dispatch(
        tripPackageInventorySlice.rejectTripPackageAsync(packageUUID, reason),
      );
    }
    setTripPackageRejectOpen(false);
    close();
  };

  useEffect(() => {
    dispatch(referenceSlice.fetchTimeZonesAsync());
  }, []);

  return (
    <StyledDialog
      open={open}
      maxWidth="xl"
      fullWidth={true}
      scroll="body"
      onClose={closeDialog}>
      <DialogTitle>{t('notification.tripPackageTitle')}</DialogTitle>
      <DialogContent>
        {/* {t('notification.tripPackageMessage')} */}
        {tripPackage && <TripPackageSummary tripPackage={tripPackage} />}
        <div>
          <DataGrid
            getRowId={(row) => row.tripLegReferenceID}
            loading={loadingTripPackage}
            rows={tripPackage?.tripLegs || []}
            columns={columns}
            autoHeight={true}
            className={classes.grid}
            checkboxSelection={false}
            disableColumnMenu={true}
            disableColumnSelector={true}
            hideFooter={true}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="contained"
          onClick={acceptTripPackage}
          disabled={acceptingTripPackage || rejectingTripPackage}>
          {t('notification.accept')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={rejectTripPackage}
          disabled={acceptingTripPackage || rejectingTripPackage}>
          {t('notification.decline')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => closeDialog()}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
      <TripPackageRejectDialog
        rejectReasons={rejectReasons}
        open={tripPackageRejectOpen}
        close={closeTripPackageReject}
      />
    </StyledDialog>
  );
}
